/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from "react";
import dayjs from "dayjs";
import { startCase } from 'lodash'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import { CircularSkeletalLoader } from "../circular_skeletal_loader";
import { StatisticSkeletalLoader } from "../statistic_skeletal_loader";
import {
  getFallbackAcronym,
  getProfileBadgeColor,
} from "../global_search_bar/helpers";
import {
  StyledGuestContactContent,
  StyledGuestContactLabel,
  StyledGuestContactRow,
  StyledGuestContactSection,
  StyledGuestContactValue,
  StyledGuestName,
  StyledGuestPersonalCard,
  StyledGuestPersonalHeader,
  StyledGuestPersonalSkeletonWrap,
  StyledGuestProfileFallback,
  StyledGuestProfileImage,
  StyledGuestSectionTitle,
  StyledGuestSince,
  StyledGuestSourceOfTruthSection,
  StyledGuestSourceOfTruthChips,
  StyledSourceOfTruthChip,
  CircularLoaderContainer,
} from "../../pages/guest_details/styles";
import { AssociatedRestaurantDiscountChip } from "../associated_guest_restaurant/styles";
import { getReadableTextColor } from "../associated_guest_restaurant/helpers";
import { StatusChipContainer, StyledStatusChip } from "./styles";

const NO_ACTIVE_EXPERIENCE_LABEL = 'No Active Experience';
const NO_INDUSTRY_CARDS_LABEL = 'No Industry Cards';
const DEFAULT_CARD_COLOR = '#556476';

export const GuestPersonalDetail = ({ personalDetail, loading }) => {
  const badgeColor = getProfileBadgeColor(personalDetail?.profile_badge);

  const joiningDateLabel = useMemo(() => {
    if (!personalDetail?.joining_date) {
      return '-';
    }

    return dayjs(personalDetail.joining_date).format('MMM YYYY');
  }, [personalDetail?.joining_date]);

  const lastExperienceDateLabel = useMemo(() => {
    if (!personalDetail?.last_working_experience) {
      return '-';
    }

    return dayjs(personalDetail.last_working_experience).format('MMM YYYY');
  }, [personalDetail?.last_working_experience]);

  const sourceOfTruth = personalDetail?.source_of_truth;
  const hasSourceOfTruth = Array.isArray(sourceOfTruth) && sourceOfTruth.length > 0;
  const industryCards = Array.isArray(personalDetail?.industry_cards) ? personalDetail.industry_cards : [];

  return (
    <StyledGuestPersonalCard data-testid="guest-personal-detail-card">
      <StyledGuestPersonalHeader>
        {loading ? (
          <StyledGuestPersonalSkeletonWrap>
            <CircularLoaderContainer>
                <CircularSkeletalLoader dataTestId="guest-personal-profile-loader" />
            </CircularLoaderContainer>
            <StatisticSkeletalLoader dataTestId="guest-personal-name-loader" />
            <StatisticSkeletalLoader dataTestId="guest-personal-joining-loader" />
          </StyledGuestPersonalSkeletonWrap>
        ) : personalDetail?.profile_picture?.imageUrl ? (
          <>
            <StyledGuestProfileImage
              src={personalDetail?.profile_picture?.imageUrl}
              alt={personalDetail?.name || 'Guest profile'}
              data-testid="guest-personal-profile-image"
            />
          </>
        ) : (
          <>
            <StyledGuestProfileFallback badgeColor={badgeColor} data-testid="guest-personal-profile-fallback">
              {personalDetail?.acronym || getFallbackAcronym(personalDetail?.name)}
            </StyledGuestProfileFallback>
          </>
        )}
        {!loading && (
          <>
            <StyledGuestName data-testid="guest-personal-name">{personalDetail?.name || '-'}</StyledGuestName>
            {
              personalDetail?.status === 'active' && (
                <StyledGuestSince data-testid="guest-personal-joining-date">
                  Since {joiningDateLabel}
                </StyledGuestSince>
              )
            }
            {
              personalDetail?.status === 'inactive' && (
                <StyledGuestSince>{`Since ${joiningDateLabel} - Until ${lastExperienceDateLabel}`}</StyledGuestSince>
              )
            }
            <StatusChipContainer>
              <StyledStatusChip 
                label={personalDetail?.status?.toUpperCase() || '-'}
                isActive={personalDetail?.status}
                data-testid="guest-personal-status"
              />
            </StatusChipContainer>
          </>
        )}
      </StyledGuestPersonalHeader>

      <StyledGuestContactSection>
        <StyledGuestSectionTitle>Contact Details</StyledGuestSectionTitle>

        <StyledGuestContactRow>
          <MailOutlineRoundedIcon sx={{ fontSize: '16px', color: '#60748c', marginTop: '2px' }} />
          <StyledGuestContactContent>
            <StyledGuestContactLabel>email</StyledGuestContactLabel>
            {loading ? (
              <StatisticSkeletalLoader dataTestId="guest-personal-email-loader" />
            ) : (
              <StyledGuestContactValue data-testid="guest-personal-email">
                {personalDetail?.email || '-'}
              </StyledGuestContactValue>
            )}
          </StyledGuestContactContent>
        </StyledGuestContactRow>

        <StyledGuestContactRow>
          <LocalPhoneOutlinedIcon sx={{ fontSize: '16px', color: '#60748c', marginTop: '2px' }} />
          <StyledGuestContactContent>
            <StyledGuestContactLabel>phone</StyledGuestContactLabel>
            {loading ? (
              <StatisticSkeletalLoader dataTestId="guest-personal-phone-loader" />
            ) : (
              <StyledGuestContactValue data-testid="guest-personal-phone">
                {personalDetail?.phone_number || '-'}
              </StyledGuestContactValue>
            )}
          </StyledGuestContactContent>
        </StyledGuestContactRow>
      </StyledGuestContactSection>

      <StyledGuestSourceOfTruthSection>
        <StyledGuestSectionTitle>Source Of Truth</StyledGuestSectionTitle>

        <StyledGuestSourceOfTruthChips data-testid="guest-personal-source-of-truth-chips">
          {loading ? (
            <StatisticSkeletalLoader dataTestId="guest-personal-source-of-truth-loader" />
          ) : hasSourceOfTruth ? (
            sourceOfTruth?.map((source) => (
              <StyledSourceOfTruthChip
                key={source}
                source={source}
                data-testid={`guest-personal-source-of-truth-chip-${source}`}
              >
                {startCase(source)}
              </StyledSourceOfTruthChip>
            ))
          ) : (
            <StyledSourceOfTruthChip
              source="default"
              data-testid="guest-personal-source-of-truth-chip-default"
            >
              {NO_ACTIVE_EXPERIENCE_LABEL}
            </StyledSourceOfTruthChip>
          )}
        </StyledGuestSourceOfTruthChips>
      </StyledGuestSourceOfTruthSection>

      <StyledGuestSourceOfTruthSection>
        <StyledGuestSectionTitle>Industry Cards</StyledGuestSectionTitle>

        <StyledGuestSourceOfTruthChips data-testid="guest-personal-industry-cards">
          {loading ? (
            <StatisticSkeletalLoader dataTestId="guest-personal-industry-cards-loader" />
          ) : industryCards.length ? (
            industryCards.map((card) => (
              <AssociatedRestaurantDiscountChip
                key={card?.id}
                label={card?.name || '-'}
                size="small"
                chipbackground={card?.color || DEFAULT_CARD_COLOR}
                chiptextcolor={getReadableTextColor(card?.color || DEFAULT_CARD_COLOR)}
                data-testid={`guest-personal-industry-card-${card?.id}`}
              />
            ))
          ) : (
            <StyledSourceOfTruthChip
              source="default"
              data-testid="guest-personal-industry-cards-empty"
            >
              {NO_INDUSTRY_CARDS_LABEL}
            </StyledSourceOfTruthChip>
          )}
        </StyledGuestSourceOfTruthChips>
      </StyledGuestSourceOfTruthSection>
    </StyledGuestPersonalCard>
  );
};
